<template>
    <header ref="headerEl" class="app-header-mobile fixed top-0 left-0 right-0 z-50 px-8 md:px-2 pt-8 pb-6 bg-transparent flex items-center w-full justify-between">
        <div class="fixed bg-view-sand-light w-full z-40 flex-col gap-4 grid !grid-rows-[0fr] transition-all top-0 overflow-hidden pt-0 left-0" :class="{'!grid-rows-[1fr] pt-24 pb-6': hamburger_content_visible}">
            <div class="overflow-hidden flex flex-col gap-8 text-intro px-8 ">
                <div class="h-4"></div>
                <NuxtLink :to="localePath('/work')">
                    <div :class="{'font-bold': siteStore.current_page === Pages.WORK}">{{ t('work') }}</div>
                </NuxtLink>
                <NuxtLink :to="localePath('/about')">
                    <div :class="{'font-bold': siteStore.current_page === Pages.ABOUT}">{{ t('about') }}</div>
                </NuxtLink>
                <NuxtLink :to="localePath('/insights')">
                    <div :class="{'font-bold': siteStore.current_page === Pages.INSIGHTS}">{{ t('insights') }}</div>
                </NuxtLink>
                <NuxtLink :to="localePath('/contact')">
                    <div :class="{'font-bold': siteStore.current_page === Pages.CONTACT}">{{ t('contact') }}</div>
                </NuxtLink>

                <NuxtLink v-show="mainStore.active_locale === 'en_GB'" :to="switchLocalePath('nl')" class="text-caption text-dark-blue/50">NL</NuxtLink>
                <NuxtLink v-show="mainStore.active_locale === 'nl_NL'" :to="switchLocalePath('en')" class="text-caption text-dark-blue/50">EN</NuxtLink>

                <div class="flex gap-2 pt-4">
                    <a href="https://nl.linkedin.com/company/yipp-interactive" target="_blank" class="w-12 h-12"><LinkedInIcon class="w-full h-full"/></a>
                    <a href="https://www.instagram.com/yippinteractive/" target="_blank" class="w-12 h-12"><InstagramIcon class="w-full h-full"/></a>
                    <a href="https://www.youtube.com/@yippinteractive" target="_blank" class="w-12 h-12"><YoutubeIcon class="w-full h-full"/></a>
                </div>
            </div>
        </div>
        <div class="md:ml-14 md:flex md:self-start z-50">
            <HeaderLogo class="z-50"/>
        </div>

        <div class="absolute left-1/2 -translate-x-1/2 rounded-full bg-neutral-200/50 flex-row gap-12 py-3 px-7 backdrop-blur justify-self-center hidden sm:!visible sm:!flex text-xs md:!text-[14px] items-center mt-2 md:mt-0">
            <NuxtLink :to="localePath('/work')">
                <div :class="{'font-bold': siteStore.current_page === Pages.WORK}">{{ t('work') }}</div>
            </NuxtLink>
            <NuxtLink :to="localePath('/about')">
                <div :class="{'font-bold': siteStore.current_page === Pages.ABOUT}">{{ t('about') }}</div>
            </NuxtLink>
<!--            <div>Approach</div>-->
<!--            <div>Insights</div>-->
            <NuxtLink :to="localePath('/insights')">
                <div :class="{'font-bold': siteStore.current_page === Pages.INSIGHTS}">{{ t('insights') }}</div>
            </NuxtLink>
            <NuxtLink :to="localePath('/contact')">
                <div :class="{'font-bold': siteStore.current_page === Pages.CONTACT}">{{ t('contact') }}</div>
            </NuxtLink>

            <div class="text-[10px] cursor-pointer">
                <div v-show="mainStore.active_locale === 'en_GB'" class="relative">
                    <NuxtLink :to="switchLocalePath('nl')" class="absolute top-0 left-0 w-8 h-7 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">NL</NuxtLink>
                </div>
                <div v-show="mainStore.active_locale === 'nl_NL'" class="relative">
                    <NuxtLink :to="switchLocalePath('en')" class="absolute top-0 left-0 w-8 h-7 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">EN</NuxtLink>
                </div>
            </div>
        </div>
        <div class="bg-neutral-200/50 backdrop-blur flex items-center justify-center w-14 h-14 rounded-lg cursor-pointer z-50 sm:hidden">
            <div class="flex justify-self-end self-center relative z-50 w-6 h-6 " ref="hamburgerButtonEl" @click="on_click_hamburger">
                <div class="absolute w-full h-full opacity-0 transition-opacity duration-150" :class="{'opacity-100': !hamburger_content_visible}">
                    <svg width="100%" height="100%" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 0.5H24" stroke="#242653"/>
                        <path d="M0 10.5H24" stroke="#242653"/>
                        <path d="M0 19.5H24" stroke="#242653"/>
                    </svg>

                </div>
                <div class="absolute w-full h-full opacity-0 transition-opacity duration-150" :class="{'opacity-100': hamburger_content_visible}">
                    <svg width="100%" height="100%" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.273438 0.416992L20.2734 19.417" stroke="#242653"/>
                        <path d="M0.273438 19.417L20.2734 0.416993" stroke="#242653"/>
                    </svg>
                </div>
            </div>
        </div>
    </header>
</template>

<script lang="ts" setup>

import { Pages } from "#shared/types/Pages"
import HeaderLogo from "~/components/view/HeaderLogo.vue"
import InstagramIcon from "~/components/icons/InstagramIcon.vue"
import LinkedInIcon from "~/components/icons/LinkedInIcon.vue"
import YoutubeIcon from "~/components/icons/YoutubeIcon.vue"

const hamburger_content_visible = ref(false)
const hamburgerButtonEl = ref()
const mainStore = useMainStore()
const siteStore = useSiteStore()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()

const { t } = useI18n({useScope: 'local'})

const props = defineProps({
    header_icon_color: {
        type: String as PropType<'light' | 'dark'>
    },
    current: String as PropType<Pages>
})
const headerEl = ref<HTMLElement | null>(null)
const offset = ref(0)

const previous_header_color = ref<'light' | 'dark' | null>()

const on_click_hamburger = () => {
    hamburger_content_visible.value = !hamburger_content_visible.value
    previous_header_color.value = siteStore.header_icon_color
    if (hamburger_content_visible.value) {
        siteStore.setIconColor('dark')
        headerEl.value?.style.setProperty('--header-offset', `0px`)
    } else {
        if (previous_header_color.value) siteStore.setIconColor(previous_header_color.value)
    }
}

const hide_hamburger_content = (event: any) => {
    if (!hamburgerButtonEl.value?.contains(event.target)) {
        hamburger_content_visible.value = false
    }
}


onMounted(() => {
    const el = headerEl.value
    if (!el) return

    const headerHeight = el.offsetHeight
    // How many px the header has been "consumed" by downward scroll.
    // Clamped to [0, headerHeight] so it never over-slides.
    let _offset = 0
    let lastY = window.scrollY

    window.addEventListener('click', hide_hamburger_content)

    window.addEventListener('scroll', () => {
        const y = Math.max(0, window.scrollY)
        const delta = y - lastY
        lastY = y
        hamburger_content_visible.value = false

        _offset = Math.min(headerHeight, Math.max(0.000, _offset + delta))
        el.style.setProperty('--header-offset', `${_offset}px`)
        offset.value = _offset
    }, { passive: true })
})

onUnmounted(() => {
    window.removeEventListener('click', hide_hamburger_content)
})

</script>

<style scoped>
.app-header-mobile {
    --header-offset: 0px;
    @media (max-width: 40rem) {
        transform: translateY(calc(-1 * var(--header-offset)));
    }
    will-change: transform;
}
.app-header {
    transform: translateY(0px) !important;
}
</style>

<i18n lang="yaml">
en:
    work: Work
    about: About
    insights: Insights
    contact: Contact
nl:
    work: Projecten
    about: Over ons
    insights: Inzichten
    contact: Contact
</i18n>
